/**
 * HINH HOC CUA MOT KHOI VAN BAN.
 *
 * TextBlock trong hop dong kieu chi mang bbox va danh sach run. Tang dat chu can
 * nhieu hon: duong co so cua DONG DAU (de chu moi ngoi dung cho chu cu), buoc
 * dong, va trang thai van ban (Tc/Tw/Th) de tinh buoc tien glyph.
 *
 * Nhung con so nay duoc SUY RA tu cac run, khong duoc bia ra. Khi khong suy ra
 * duoc (khoi rong, bbox suy bien) ta tra ve null va ben goi phai tu choi -
 * doan bua mot buoc dong se dat chu chong len nhau ma khong ai bao loi.
 */
import type { PdfRect, TextBlock, TextRun } from '@pdfeditor/types';

export interface BlockGeometry {
  bbox: PdfRect;
  fontSizePt: number;
  /** Buoc dong: khoang cach doc giua hai duong co so lien tiep. */
  lineHeightPt: number;
  /** Tc */
  charSpacing: number;
  /** Tw */
  wordSpacing: number;
  /** Th, phan tram */
  horizontalScale: number;
  colorRgb: readonly [number, number, number];
  /** Toa do x noi dong dau bat dau. */
  firstLineX: number;
  /** Toa do y cua duong co so dong dau. */
  firstBaselineY: number;
  /**
   * So dong THAT cua khoi goc, dem theo cac duong co so phan biet.
   *
   * Can cho viec tinh hop che: duong co so dong CUOI = firstBaselineY -
   * (lineCount - 1) * lineHeightPt, va day mois la moc de biet dau/duoi chu
   * cu tut xuong toi dau.
   */
  lineCount: number;
}

/** Duong co so cua mot run = phan tu f cua ma tran van ban. */
function baselineOf(run: TextRun): number {
  return run.matrix[5];
}

/** Trung vi - ben vung truoc mot vai run lac loai, khac voi trung binh. */
function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Suy ra hinh hoc dat chu tu mot TextBlock.
 * Tra ve null khi khoi khong du du lieu de dat chu mot cach xac dinh.
 */
export function deriveBlockGeometry(block: TextBlock): BlockGeometry | null {
  const runs = block.runs.filter((r) => r.text.length > 0);
  if (runs.length === 0) return null;
  if (!(block.bbox.width > 0) || !(block.bbox.height > 0)) return null;

  // Run dau tien theo thu tu doc: duong co so cao nhat, roi trai nhat.
  const ordered = [...runs].sort((a, b) => {
    const dy = baselineOf(b) - baselineOf(a);
    if (Math.abs(dy) > 0.5) return dy;
    return a.matrix[4] - b.matrix[4];
  });
  const first = ordered[0];
  if (!first) return null;

  const fontSizePt = first.fontSizePt > 0 ? first.fontSizePt : block.bbox.height;
  if (!(fontSizePt > 0)) return null;

  // Buoc dong tu khoang cach giua cac duong co so PHAN BIET. Mot dong gom
  // nhieu run cung duong co so, gom chung lai truoc khi do khoang cach.
  const baselines: number[] = [];
  for (const run of ordered) {
    const b = baselineOf(run);
    const last = baselines[baselines.length - 1];
    if (last === undefined || Math.abs(last - b) > Math.max(1, fontSizePt * 0.4)) {
      baselines.push(b);
    }
  }
  const gaps: number[] = [];
  for (let i = 1; i < baselines.length; i++) {
    const gap = baselines[i - 1] - baselines[i];
    if (gap > 0) gaps.push(gap);
  }
  // Khoi mot dong khong co khoang cach nao de do. 1.2 lan co chu la buoc dong
  // mac dinh cua hau het cong cu soan thao - day la mot GIA DINH, va no chi
  // duoc dung khi tai lieu khong cho ta so lieu nao tot hon.
  const lineHeightPt = gaps.length > 0 ? median(gaps) : fontSizePt * 1.2;

  return {
    bbox: block.bbox,
    fontSizePt,
    lineHeightPt,
    charSpacing: first.charSpacing,
    wordSpacing: first.wordSpacing,
    horizontalScale: first.horizontalScale > 0 ? first.horizontalScale : 100,
    colorRgb: first.colorRgb,
    firstLineX: first.matrix[4],
    firstBaselineY: baselineOf(first),
    lineCount: Math.max(1, baselines.length),
  };
}
